import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { map, each, get, contains, filter } from 'underscore';
import Promise from 'bluebird';
import { makeStyles, shorthands, } from '@fluentui/react-components';
import { stringifySync } from '@bingads-webui/safe-json';
import { parseFileName, generatePreviewArray, updateFilePreviewArrayUploadProgress, updateProgress, MaximumImageFileSizeMB, MaximumVideoFileSizeMB, } from '@bingads-webui-campaign-react/upload-file-picker';
import { runJobsInParallelWithRateLimit } from '@bingads-webui-universal/parallel-job-with-rate-limit';
import {
  getImageValidationErrorMessage,
} from '@bingads-webui-campaign/image-experience-utils';
import { MaxAddAssetJobInParell, supportedImageExtensions, supportedVideoExtensions } from '../model';
import { getAssetUploadError } from '../utils';
import { useUploadAsset } from './use-upload-asset';
import { useReadAsset, useValidateAsset } from './use-validate-asset';
import { useCustomizedHandlerOptions } from './use-customized-handler-options';

const MAX_UPSCALED_SIZE = 4;

const useStyles = makeStyles({
  linkButton: {
    color: '#337ab7',
    fontWeight: 'normal',
    backgroundColor: 'transparent',
    ...shorthands.borderRadius('0px', '0px', '0px', '0px'),
    ...shorthands.border('none'),
  },
});

export const useUploadFiles = ({
  logger, dataService, perfMarker, notificationApi, i18n, onFilesAddedCallback, aid, setErrors, filePickerI18n, uploadCallback, isUploadImageUpscaleEnabled,
}) => {
  const [previewArr, setPreviewArr] = useState([]);
  const { readFileAsync } = useReadAsset();
  const { validateFileAsync } = useValidateAsset();
  const { uploadFileAsync } = useUploadAsset({ dataService, perfMarker, isUploadImageUpscaleEnabled });
  const { getCustomizedHandlerOptions } = useCustomizedHandlerOptions();
  const classes = useStyles();

  const onFileRead = useCallback((selectedFiles, callback) => {
    setErrors([]);

    const isMultipleFiles = selectedFiles.length > 1;
    const customizedOptions = map(selectedFiles, (file) => {
      const { nameWithoutExt, ext } = parseFileName(file.name);

      const passedOptions = getCustomizedHandlerOptions({
        nameWithoutExt,
        ext,
      });

      return {
        ...passedOptions,
        nameWithoutExt,
      };
    });

    // there is a perf concern for large video file
    // previous design is reading files first and then process
    // for video, may need different handling, will address it later
    return Promise.map(
      selectedFiles,
      (fileObj, index) => readFileAsync(fileObj, customizedOptions[index])
    ).then((fileSources) => {
      const fileAdditionalInfoArr = [];
      const validateFilePromise = Promise.map(
        fileSources,
        (fileSource, index) =>
          validateFileAsync({
            isUploadImageUpscaleEnabled,
            dataURL: fileSource,
            ...customizedOptions[index],
          }).then((validatedResult) => {
            const isImageUpscaled = get(validatedResult, 'isImageUpscaled', false);
            const { nameWithoutExt } = customizedOptions[index];
            fileAdditionalInfoArr[index] = {
              isImageUpscaled,
              text: nameWithoutExt,
            };
          }).catch((e) => {
            if (e && e.message === 'SizeValidationError' && logger && logger.logTrace) {
              logger.logTrace(`Add Media Panel per file - SizeValidationError: ${stringifySync(e)}`);
              logger.logTrace(`Add Media Panel per file - SizeValidationError - ImageSource: ${stringifySync(fileSource)}`);
            }
            throw e;
          })
      );

      const doubleValidateFilePromise = validateFilePromise
        .then(() => {
          const needUpscaledImage = filter(fileAdditionalInfoArr, item => item && item.isImageUpscaled);
          if (needUpscaledImage.length > MAX_UPSCALED_SIZE) {
            // When the need upscaled image count over the max size, should skip the upscale feature and use original threshold to validate again.
            return Promise.map(fileSources, (fileSource, index) => {
              if (fileAdditionalInfoArr[index].isImageUpscaled === true) {
                return validateFileAsync({
                  isUploadImageUpscaleEnabled: false,
                  dataURL: fileSource,
                  ...customizedOptions[index],
                }).catch((e) => {
                  if (e && e.message === 'SizeValidationError' && logger && logger.logTrace) {
                    logger.logTrace(`Add Media Panel per file - SizeValidationError: ${stringifySync(e)}`);
                    logger.logTrace(`Add Media Panel per file - SizeValidationError - ImageSource: ${stringifySync(fileSource)}`);
                  }
                  throw e;
                });
              }
              return Promise.resolve();
            });
          }
          return Promise.resolve();
        });

      return doubleValidateFilePromise.then(() => {
        const sourcesForPreview = generatePreviewArray({
          files: selectedFiles,
          fileSources,
          customizedOptions,
        });

        setPreviewArr(sourcesForPreview);

        const setProgress = (index, progress) => {
          setPreviewArr((prevArr) => {
            const newState =
                updateFilePreviewArrayUploadProgress(index, progress)({ previewArr: prevArr });

            return newState.previewArr;
          });
        };

        const uploadBatchs = [];

        each(fileSources, (fileSource, index) => {
          const { nameWithoutExt } = customizedOptions[index];

          uploadBatchs.push(() => uploadFileAsync({
            dataURL: fileSource,
            fileObject: selectedFiles[index],
            updateCurrentProgress: updateProgress({
              setProgress,
              index,
              uploadProgressBoundary: 1,
            }),
            uploadCallback,
            nameWithoutExt,
            ...customizedOptions[index],
          }));
        });

        const uploadErrorCodeArr = [];
        const handleJobError = (err) => {
          const errorCode = get(err, ['body', 'value', 0, 'Code'], '');

          // when errorCode is empty, it means some server error, e.g. 500 response
          uploadErrorCodeArr.push(errorCode);
        };
        return runJobsInParallelWithRateLimit({
          jobsToRateLimit: uploadBatchs,
          maxJobsInParallel: MaxAddAssetJobInParell,
          handleJobError,
        })
          .then((uploadResult) => {
            const errorMsg = getAssetUploadError({
              uploadErrorCodeArr,
              filePickerI18n,
              aid,
              logger,
              i18n,
            });

            if (errorMsg) {
              onFilesAddedCallback();
              setPreviewArr([]);
              notificationApi.showWarning(errorMsg);
              logger.logTrace(`Add Media Panel: failed to upload assets to folder, error: ${errorMsg}`);
            } else {
              const assetIds = uploadResult && uploadResult.join(',');
              logger.logTrace('Add Media Panel: saved uploaded assets to folder', { assetIds });

              const upscaledImagesArr = filter(fileAdditionalInfoArr, item => item && item.isImageUpscaled);
              const upscaledImagesArrCount = upscaledImagesArr.length;

              if (upscaledImagesArrCount > 0) {
                notificationApi.showInfo(i18n.getString(_TL_('Your assets were uploaded and we upscaled {{fileCount}} image(s) to meet the minimum required file size. You can always upload higher quality images if needed.'), { fileCount: upscaledImagesArrCount }));
              } else {
                notificationApi.showInfo((
                  <span>
                    {i18n.getString(_TL_('Your assets were successfully uploaded.'))}
                    <button className={classNames(`HelpTopic ${classes.linkButton}`)} query="help:app54863">{i18n.getString(_TL_('Learn more about asset guidelines.'))}</button>
                  </span>
                ));
              }
              onFilesAddedCallback();
              setPreviewArr([]);
            }
          });
      });
    }).catch((e) => {
      const genericErrorMsg = i18n.getString(_TL_('We\'ve encountered an error and can\'t continue.'));
      const newErrors = [
        getImageValidationErrorMessage(e, filePickerI18n, isMultipleFiles, isUploadImageUpscaleEnabled /* skipDimension */) || genericErrorMsg,
      ];
      setPreviewArr([]);
      setErrors(newErrors);

      logger.logTrace(`Add Media Panel: failed to upload assets to folder, error: ${newErrors[0]}`);
      if (e && e.message === 'SizeValidationError' && logger && logger.logTrace) {
        logger.logTrace(`Add Media Panel - SizeValidationError: ${stringifySync(e)}`);
      }
    }).finally(() => {
      callback();
    });
  }, [setErrors, getCustomizedHandlerOptions, readFileAsync, validateFileAsync, logger, uploadFileAsync, uploadCallback, filePickerI18n, aid, onFilesAddedCallback, notificationApi, i18n, classes.linkButton, isUploadImageUpscaleEnabled]);

  return {
    onFileRead,
    previewArr,
  };
};

export const isReachFileSizeLimit = ({ ext, fileSizeMB }) => {
  let limitMB;

  if (contains(supportedImageExtensions, ext)) {
    limitMB = MaximumImageFileSizeMB;
  } else if (contains(supportedVideoExtensions, ext)) {
    limitMB = MaximumVideoFileSizeMB;
  }

  return limitMB > 0 && fileSizeMB > limitMB;
};

export const getReachFileSizeLimitErrorMessage = ({ i18n }) =>
  i18n.getString(
    _TL_('One or more assets exceed the maximum file size. Please upload images that are smaller than {{maximumImageFileSize}} MB and videos that are smaller than {{maximumVideoFileSize}} MB.'),
    {
      maximumImageFileSize: MaximumImageFileSizeMB,
      maximumVideoFileSize: MaximumVideoFileSizeMB,
    }
  );
